import { useEffect, useState, useMemo, useRef } from "react";
import { ColumnDef, Table as TanTable } from "@tanstack/react-table";
import { AddIcon } from "@chakra-ui/icons";
import {
  batchDownload,
  convertReport,
  deleteAssessment,
  downloadAssessmentReport,
  generateAssessmentReport,
  getAssessments,
  mailReport,
  newAssessment,
  resendAssessmentMail
} from "../../services/assessments-service";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Tag,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
  Center,
  Spinner,
  Input,
  Select,
  Text
} from "@chakra-ui/react";

import { PageHeading, Panel, Table, PhoneInput } from "@bemap/ui";
import { formatDate, IAssessment, IProfile, constants, ANALYST_ROUTES, handleAssessmentStatus, handleAssessmentTag, handleReportType, useAuth, openUrl } from "@bemap/utils";
import { useTranslation } from "react-i18next";
import { IoEllipse } from "react-icons/io5";
import { MdDownload, MdAnalytics } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { AssessmentActions } from "@bemap/ui";

const AssessmentManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const initialProfile: IProfile = {
    name: '',
    email: '',
    phone: ''
  };

  const initialAssessment: IAssessment = {
    team_id: parseInt(`${user?.currentTeamId}`),
    report_id: 0,
    client_can_unlock: false
  }

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [massDownloading, setMassDownloading] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete">();
  const [assessments, setAssessments] = useState<IAssessment[]>([]);
  const [assessment, setAssessment] = useState<IAssessment>(initialAssessment);
  const [profile, setProfile] = useState<IProfile>(initialProfile);
  const [updateData, setUpdateData] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<IAssessment[]>([]);
  const [tableInstance, setTableInstance] = useState<TanTable<IAssessment> | null>(null);

  async function copy(v: IAssessment) {
    await navigator.clipboard.writeText(`https://assessments.bemap.com.br/profile/${v.hash}`);
    toast({
      title: t('general.copied') || 'Copied!',
      status: 'success',
      isClosable: true
    });
  }

  const handleRowSelect = (assessment: IAssessment) => {
    setSelectedRows((prevSelected) => {
      if (prevSelected.includes(assessment)) {
        return prevSelected.filter((row) => row !== assessment);
      } else {
        return [...prevSelected, assessment];
      }
    });
  };

  const columns = useMemo<ColumnDef<IAssessment, any>[]>(
    () => [
      {
        id: "select",
        header: () => {
          const filteredRows = tableInstance?.getRowModel().rows.map(row => row.original) || [];
          const filteredSelectedRows = selectedRows.filter(row => filteredRows.includes(row));

          return (
            <input
              type="checkbox"
              onChange={(e) => {
                if (e.target.checked) {
                  // Add filtered rows to selected rows, avoiding duplicates
                  const newSelections = [...new Set([...selectedRows, ...filteredRows])];
                  setSelectedRows(newSelections);
                } else {
                  // Remove filtered rows from selected rows
                  const newSelections = selectedRows.filter(row => !filteredRows.includes(row));
                  setSelectedRows(newSelections);
                }
              }}
              checked={filteredRows.length > 0 && filteredSelectedRows.length === filteredRows.length}
              ref={(el) => {
                if (el) {
                  el.indeterminate = filteredSelectedRows.length > 0 && filteredSelectedRows.length < filteredRows.length;
                }
              }}
            />
          );
        },
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRows.includes(row.original)}
            onChange={() => handleRowSelect(row.original)}
            disabled={row.original.status != 'generated' || !row.original.key}
          />
        ),
      },
      {
        accessorFn: (row) => row?.profile?.name,
        id: "Nome",
        header: t('analyst.assesments_table_name') || 'Name',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row?.profile?.email,
        id: "E-mail",
        header: "E-MAIL",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row?.company?.name || row?.profile?.listProfiles?.[0]?.company?.name || '--',
        id: "Empresa",
        header: t('analyst.assesments_table_company') || 'Company',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.profile?.listProfiles?.[0]?.listProfile?.name || '--',
        id: "Grupo",
        header: t('analyst.assesments_table_group') || 'Group',
        cell: (info) => {
          return info.getValue();
        },
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.results?.dominantProfiles?.short,
        id: "Perfil Comportamental",
        header: t('analyst.assesments_table_profile') || 'Profile',
        filterFn: 'equalsString',
        cell: (info) => {
          return info.getValue() ? (
            <Tag size='sm' variant={'outline'} colorScheme={'blue'}>
              {info.getValue()}
            </Tag>
          ) : '--';
        },
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => formatDate(row.created_at || ''),
        id: "Criado em",
        header: t('analyst.assesments_table_created') || 'Created',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => t(handleReportType(row.report_id || 0) || ''),
        id: "Tipo de Relatório",
        header: t('analyst.assesments_table_type') || 'Type',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => t(handleAssessmentStatus(row.status || 'unknown', row.results?.error) || ''),
        id: "Status",
        header: "STATUS",
        cell: (info) => {
          // Ensure handleAssessmentStatus always returns a string
          const status: string = handleAssessmentStatus(
            info.row.original.status ?? 'unknown',
            info.row.original.results?.error
          ) || '';

          // Ensure handleAssessmentTag always receives a string
          const colorScheme: string = handleAssessmentTag(status) || 'gray';

          return (
            <Tooltip label={info.getValue()}>
              <Tag size='sm' colorScheme={colorScheme}>
                <IoEllipse />
              </Tag>
            </Tooltip>
          );
        },
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions') || 'Actions',
        cell: (row) => {
          const assessment = row.getValue();
          return (
            <AssessmentActions
              report={assessment}
              resendAction={() => resendAssessmentLink(assessment.id!)}
              copyAction={() => copy(assessment)}
              downloadAction={() => downloadReport(assessment.hash)}
              generateAction={() => generateAssessment(assessment.hash)}
              convertAction={() => unlockReport(assessment.hash)}
              mailAction={() => sendMailReport(assessment.hash)}
              viewAction={() => navigate(`/assessments/${assessment.id}/results`)}
            />
          );
        },
      },
    ],
    [assessments, selectedRows, t, tableInstance]
  );

  useEffect(() => {
    if (profile.name.trim() && profile.email.trim() && (profile.phone || '').trim() && assessment.report_id != 0) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [assessment, profile]);

  useEffect(() => {
    loadAssessments();
  }, [updateData]);

  function closeModal() {
    onClose();
    setAssessment(initialAssessment);
    setProfile(initialProfile);
  }

  async function loadAssessments() {
    setIsLoading(true);
    try {
      const response = await getAssessments();
      setAssessments(response.message);
      setIsLoading(false);
    } catch (error) {
      setAssessments([]);
      setIsLoading(false);
    }
  }

  async function createNewAssessment() {
    setIsSaving(true);
    toast({
      title: t('general.saving') || 'Saving...',
      status: 'loading',
      isClosable: true,
    });
    try {
      const new_assessment = {
        ...assessment,
        profile: {
          ...profile
        }
      };
      await newAssessment(new_assessment);
      toast.closeAll();
      setIsSaving(false);
      closeModal();
      setUpdateData(new Date().toLocaleString());
      setProfile(initialProfile);
      setAssessment(initialAssessment);
    } catch (error) {
      toast.closeAll();
      toast({
        title: t('general.error') || 'Error',
        status: 'error',
        isClosable: true,
      });
      setIsLoading(false);
      setIsSaving(false);
    }
  }

  const handleDeleteAssessment = async () => {
    toast({
      title: t('general.deleting') || 'Deleting...',
      status: 'loading',
      isClosable: true,
    });
    try {
      await deleteAssessment(assessment.id!);
      setUpdateData(new Date().toLocaleString());
      closeModal();
    } catch (error) {
      toast.closeAll();
      toast({
        title: t('general.error') || 'Error',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  async function resendAssessmentLink(id: number) {
    toast({
      title: t('general.loading') || 'Loading...',
      status: 'loading',
      isClosable: true,
    });
    setIsSaving(true);
    try {
      await resendAssessmentMail(id);
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      toast({
        title: t('general.error') || 'Error',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }

  async function unlockReport(hash: string) {
    toast({
      title: t('general.loading') || 'Loading...',
      status: 'loading',
      isClosable: true,
    });
    setIsSaving(true);
    try {
      const response = await convertReport(hash);
      toast.closeAll();
      setUpdateData(new Date().toLocaleString());
      openUrl(response.message)
      setIsSaving(false);
    } catch (error) {
      toast.closeAll();
      setIsSaving(false);
      toast({
        title: t('general.loading') || 'Loading...',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }

  async function sendMailReport(hash: string) {
    try {
      toast({
        title: t('general.loading') || 'Loading...',
        status: 'loading',
        isClosable: true,
      });
      setIsSaving(true);
      await mailReport(hash);
      toast.closeAll();
      toast({
        title: t('general.success') || 'Success',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setIsSaving(false);
    } catch (error) {
      toast.closeAll();
      setIsSaving(false);
      toast({
        title: t('general.error') || 'Error',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }

  async function generateAssessment(hash: string) {
    toast({
      title: t('general.loading') || 'Loading...',
      status: 'loading',
      duration: 9000,
      isClosable: true,
    });
    setIsSaving(true);
    try {
      const response = await generateAssessmentReport(hash);
      toast.closeAll();
      openUrl(response.message)
      setUpdateData(new Date().toLocaleString());
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      toast.closeAll();
      setUpdateData(new Date().toLocaleString());
      toast({
        title: t('general.error') || 'Error',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }

  async function downloadReport(hash: string) {
    setIsSaving(true);
    try {
      const { message } = await downloadAssessmentReport(hash);
      openUrl(message)
      setIsSaving(false);
    } catch (error) {
      setIsSaving(false);
      toast({
        title: t('general.error') || 'Error',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }

  async function postBatchDownload() {
    setMassDownloading(true);
    try {
      const filteredRows = selectedRows.filter(row => row.status === 'generated');
      const hashes = filteredRows.map(row => row.key).filter((key): key is string => key !== undefined);

      const response = await batchDownload(hashes);
      const blob = new Blob([response], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      const currentDate = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
      link.href = url;
      link.download = `export_assessments_${currentDate}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setMassDownloading(false);
    } catch (error) {
      setMassDownloading(false);
      toast({
        title: t('general.invalid_csv') || 'Invalid CSV',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }

  async function handleChangeProfile(event: any) {
    setProfile({
      ...profile,
      [event.target.name]: event.target.value,
    });
  }

  function handleGroupAnalysis() {
    const validAssessments = selectedRows.filter(row => row.status === 'generated' && row.results);
    if (validAssessments.length < 2) {
      toast({
        title: t('analyst.group_analysis_min_error') || 'Minimum assessments required',
        description: t('analyst.group_analysis_min_description') || 'You need at least 2 completed assessments for group analysis',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const assessmentIds = validAssessments.map(assessment => assessment.id);
    navigate(ANALYST_ROUTES.ASSESSMENTS_GROUP, { state: { assessmentIds } });
  }

  function RenderModal() {
    switch (modalType) {
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal} closeOnOverlayClick={false}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('analyst.assesments_new') || 'New Assessment'}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction={"column"} gap={4}>
                  <FormControl>
                    <FormLabel>{t('analyst.assesments_new_client') || 'Client Name'}</FormLabel>
                    <Input
                      name="name"
                      placeholder={t('general.placeholder_name') || 'Enter name'}
                      type="text"
                      onChange={handleChangeProfile}
                      value={profile.name || ""}
                      required
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>E-mail</FormLabel>
                    <Input
                      name="email"
                      placeholder={t('general.placeholder_email') || 'Enter email'}
                      type="email"
                      onChange={handleChangeProfile}
                      value={profile.email || ""}
                      required
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      {t('analyst.assesments_new_email_text') || 'Assessment link will be sent to this email'}
                    </Text>
                  </FormControl>
                  <PhoneInput
                    value={profile.phone || ""}
                    onChange={(phone) => setProfile({ ...profile, phone })}
                  />
                  <FormControl>
                    <FormLabel>{t('analyst.assesments_new_type') || 'Report Type'}</FormLabel>
                    <Select
                      name="report_id"
                      defaultValue="default"
                      onChange={(e) => setAssessment({
                        ...assessment,
                        [e.target.name]: e.target.value
                      })}
                    >
                      <option value="default" disabled hidden>
                        {t('actions.click_to_select') || 'Click to select'}
                      </option>
                      {constants.reports.map((report) => {
                        return (
                          <option label={t(report.name)} key={`${report.name}`} value={report.value}>
                            {t(report.name)}
                          </option>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl display='flex' alignItems='center' gap="20px">
                    <Switch id='client_can_unlock' colorScheme={'blue'} onChange={(e) => setAssessment({ ...assessment, client_can_unlock: e.target.checked })} />
                    <FormLabel htmlFor='client_can_unlock' mb='0'>
                      {t('analyst.assesments_new_unlock') || 'Client can unlock report'}
                    </FormLabel>
                  </FormControl>
                  <Accordion allowToggle mt={3}>
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          {t('actions.additional_info') || 'Additional Information'}
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <FormControl id="company">
                          <FormLabel>{t('analyst.company') || 'Company'}</FormLabel>
                          <Input
                            type="text"
                            placeholder={t('general.placeholder_company') || 'Enter company name'}
                            onChange={(e) => {
                              setAssessment((prev) => ({
                                ...prev,
                                company: {
                                  name: e.target.value
                                },
                              }));
                            }}
                          />
                        </FormControl>
                        <FormControl id="birth" mt={4}>
                          <FormLabel>{t('analyst.people_birth_date') || 'Birth Date'}</FormLabel>
                          <Input
                            type="date"
                            onChange={(e) => {
                              setProfile((prev) => ({
                                ...prev,
                                metadata: { ...prev.metadata, birth: new Date(e.target.value).toISOString() },
                              }));
                            }}
                          />
                        </FormControl>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Flex>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel') || 'Cancel'}
                </Button>
                <Button colorScheme={"blue"} onClick={createNewAssessment} isLoading={isSaving} isDisabled={!isValid}>
                  {t('analyst.assesments_action_send')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('actions.delete_assessment') || 'Delete Assessment'}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('actions.delete_confirm') || 'Are you sure? This action cannot be undone.'}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel') || 'Cancel'}
                  </Button>
                  <Button colorScheme='red' onClick={handleDeleteAssessment} ml={3}>
                    {t('actions.delete_confirm_button') || 'Delete'}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        );
      default:
        return null;
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  const actions = [
    {
      label: t('analyst.assesments_new'),
      icon: <AddIcon />,
      colorScheme: 'blue',
      onClick: () => {
        onOpen();
        setModalType("new");
      },
    },
    {
      label: t('analyst.download_mass'),
      icon: <MdDownload />,
      colorScheme: 'blue',
      variant: 'outline',
      isDisabled: selectedRows.length < 1,
      isLoading: massDownloading,
      onClick: postBatchDownload,
    },
    {
      label: t('analyst.group_analysis'),
      icon: <MdAnalytics />,
      colorScheme: 'green',
      variant: 'outline',
      isDisabled: selectedRows.filter(row => row.status === 'generated' && row.results).length < 2,
      onClick: handleGroupAnalysis,
    },
  ];

  return (
    <>
      <VStack w="100%" gap={4} align="stretch">
        <PageHeading title={t('analyst.assesments')} actions={actions} />
        <Panel>
          <Table
            tableId="analyst-assessments-table"
            columns={columns}
            data={assessments}
            onTableReady={setTableInstance}
          />
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default AssessmentManagement; 